<template>
  <card-dialog
    v-model="modalVisible"
    :persistent="false"
    max-width="500px"
    :title="$t('add_new.dialog.header')"
  >
    <v-card-text
      class="pa-6"
    >
      <v-list class="pa-0 ma-0 v-list--bordered">
        <squared-button
          v-if="!hasNoEvents && $can('create-event')"
          icon="fa-light fa-calendar-days"
          :text="$t('add_new.event')"
          @click="onAddNewClick($events.OPEN_DIALOG_EVENT_FORM)"
        />
        <squared-button
          v-if="!hasNoEvents && hasEventGroups && $can('create-event')"
          icon="fa-light fa-trophy"
          :text="$t('add_new.tournament')"
          @click="onAddNewClick($events.OPEN_DIALOG_TOURNAMENT_FORM)"
        />
        <squared-button
          v-if="hasMedicalModule && !hasNoPlayers && $can('create-medical')"
          icon="fa-light fa-truck-medical"
          :text="$t('add_new.injury')"
          @click="onAddNewClick($events.OPEN_DIALOG_INJURY_FORM)"
        />
        <squared-button
          v-if="hasMedicalModule && !hasNoPlayers && $can('create-medical')"
          icon="fa-light fa-temperature-high"
          :text="$t('add_new.illness')"
          @click="onAddNewClick($events.OPEN_DIALOG_ILLNESS_FORM)"
        />
        <squared-button
          v-if="$can('create-diagnostics') && hasDiagnosticsModule && !hasNoPlayers"
          icon="fa-light fa-weight"
          :text="$t('add_new.results')"
          @click="onAddNewClick($events.OPEN_DIALOG_DIAGNOSTIC_RESULTS)"
        />
        <squared-button
          v-if="hasDiagnosticsModule && !hasNoPlayers"
          icon="fa-light fa-filter-list"
          :text="$t('add_new.diagnostic_protocol')"
          @click="onAddNewClick($events.OPEN_DIALOG_PROTOCOL_FORM)"
        />
        <squared-button
          v-if="hasTrainingBuilderModule && !hasNoPlayers && $can('create-training-protocols')"
          icon="fa-light fa-list-check"
          :text="$t('add_new.training')"
          @click="onAddNewClick($events.OPEN_DIALOG_TRAINING_PROTOCOL_FORM)"
        />
        <squared-button
          v-if="hasTrainingBuilderModule && !hasNoPlayers"
          icon="fa-light fa-dumbbell"
          :text="$t('add_new.exercise')"
          @click="onAddNewClick($events.OPEN_DIALOG_TRAINING_EXERCISE_FORM)"
        />
        <squared-button
          v-if="hasTrainingProgramsModule && !hasNoPlayers"
          icon="fa-light fa-list-check"
          :text="$t('add_new.training_program')"
          @click="onAddNewClick($events.OPEN_DIALOG_TRAINING_PROGRAM_FORM)"
        />
        <squared-button
          v-if="!hasNoPlayers"
          icon="fa-light fa-poll"
          :text="$t('add_new.evaluation')"
          @click="onAddNewClick($events.OPEN_DIALOG_QUESTIONNAIRE_INSTANCE_FORM)"
        />
        <v-divider v-if="isAdmin || canManageTeamPlayers" />
        <squared-button
          v-if="(isAdmin || canManageTeamPlayers) && !hasNoPlayers"
          icon="fa-light fa-user"
          :text="$t('add_new.player')"
          :to="{ name: $routeNames.ADMIN_PLAYER_FORM }"
          @click="modalVisible = false"
        />
        <squared-button
          v-if="isAdmin && !hasNoPlayers"
          icon="fa-light fa-users"
          :text="$t('add_new.team') "
          @click="onAddNewClick($events.OPEN_DIALOG_TEAM_FORM)"
        />
        <squared-button
          v-if="isAdmin && !hasNoStaff"
          icon="fa-light fa-user-tie"
          :text="$t('add_new.staff')"
          @click="onAddNewClick($events.OPEN_DIALOG_COACH_FORM)"
        />
        <squared-button
          v-if="isAdmin && !hasNoEvents"
          icon="fa-light fa-map"
          :text="$t('add_new.location')"
          @click="onAddNewClick($events.OPEN_DIALOG_LOCATION_FORM)"
        />
        <squared-button
          v-if="isAdmin && hasMembershipModule && !hasNoPlayers && $can('create-membership')"
          icon="fa-light fa-receipt"
          :text="$t('add_new.membership_plan')"
          @click="onAddNewClick($events.OPEN_DIALOG_MEMBERSHIP_PLAN_FORM)"
        />
      </v-list>
    </v-card-text>
  </card-dialog>
</template>

<script>
import { EventBus } from '@/plugins/bus'
import CardDialog from '@/components/helpers/CardDialog'
import SquaredButton from '@/components/buttons/SquaredButton'

export default {
  name: 'SelectFormDialog',
  components: { SquaredButton, CardDialog },
  props: {
    player: {
      type: Object,
      default: () => ({})
    },
    parentPlayer: {
      type: Object,
      default: () => undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isAdmin() {
      return this.$can('manage-organisation')
    },
    canManageTeamPlayers() {
      return this.$can('manage-team-players')
    },
    hasTrainingBuilderModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_training_builder_module
    },
    hasMedicalModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_medical_module
    },
    hasMembershipModule() {
      return this.user.organisation_settings && this.user.organisation_settings.has_membership_module
    },
    hasTrainingProgramsModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_training_programs_module
    },
    hasDiagnosticsModule() {
      return this.user.organisation_settings?.has_diagnostics_module || false
    },
    hasNoPlayers() {
      return this.user.organisation_settings?.has_no_players || false
    },
    hasNoStaff() {
      return this.user.organisation_settings?.has_no_staff || false
    },
    hasNoEvents() {
      return this.user.organisation_settings?.has_no_events || false
    },
    hasEventGroups() {
      return this.user.organisation_settings?.has_event_groups || false
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_SELECT_FORM, () => {
      this.modalVisible = true
    })
  },
  methods: {
    onAddNewClick(event) {
      EventBus.$emit(event)
      this.toggleModal(false)
    },
    toggleModal(val) {
      this.modalVisible = val
    }
  }
}
</script>
